import React from 'react';
import {
  Box, Spinner, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { OralExamDriver } from '@/common/api/models/oralExamDriver';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';

import { AiExaminerPresence } from './AiExaminerPresence';
import { ReconnectOverlay, useTransportReconnectOverlayVisible } from './ReconnectOverlay';
import {
  studentRuntimeJourneyTestId,
  type StudentRuntimeJourney,
} from '../utils/deriveStudentRuntimeJourney';

/** Fixed orb footprint for waiting/preparing handoff — prevents layout flash between states. */
export const AI_EXAMINER_HANDOFF_ORB_SIZE = 160;

export type StudentRuntimeJourneyScreenProps = {
  journey: Exclude<
  StudentRuntimeJourney,
  'in_exam_committed' | 'pre_start_waiting'
  >;
  driver?: OralExamRunStateResponse['driver'];
  isReconnecting?: boolean;
};

type JourneyCopy = {
  titleKey: string;
  titleDefault: string;
  bodyKey?: string;
  bodyDefault?: string;
};

function isAiExaminerHandoffJourney(
  journey: StudentRuntimeJourneyScreenProps['journey'],
  driver?: OralExamRunStateResponse['driver'],
): journey is 'waiting_for_examiner' | 'examiner_preparing' {
  return driver === OralExamDriver.examiner_agent
    && (journey === 'waiting_for_examiner' || journey === 'examiner_preparing');
}

function journeyCopy(
  journey: StudentRuntimeJourneyScreenProps['journey'],
): JourneyCopy {
  switch (journey) {
    case 'moving_to_exam_room':
      return {
        titleKey: 'oralExam.student.runtimeJourney.movingToExamRoom.title',
        titleDefault: 'Moving you to your exam room',
      };
    case 'reconnecting':
      return {
        titleKey: 'oralExam.student.runtimeJourney.reconnecting.title',
        titleDefault: 'Reconnecting to your exam room',
      };
    case 'waiting_for_examiner':
      return {
        titleKey: 'oralExam.student.runtimeJourney.waitingForExaminer.title',
        titleDefault: 'Preparing your oral exam',
        bodyKey: 'oralExam.student.runtimeJourney.waitingForExaminer.body',
        bodyDefault: 'The AI examiner is joining your room.',
      };
    case 'examiner_preparing':
      return {
        titleKey: 'oralExam.student.runtimeJourney.examinerPreparing.title',
        titleDefault: 'Preparing your oral exam',
        bodyKey: 'oralExam.student.runtimeJourney.examinerPreparing.body',
        bodyDefault: 'The AI examiner is preparing your first question.',
      };
    case 'lecturer_preparing':
      return {
        titleKey: 'oralExam.student.runtimeJourney.lecturerPreparing.title',
        titleDefault: 'Your supervisor is preparing your exam',
      };
    default:
      return {
        titleKey: 'oralExam.student.runtimeJourney.waitingForExaminer.title',
        titleDefault: 'Preparing your oral exam',
        bodyKey: 'oralExam.student.runtimeJourney.waitingForExaminer.body',
        bodyDefault: 'The AI examiner is joining your room.',
      };
  }
}

type AiExaminerHandoffShellProps = {
  journey: 'waiting_for_examiner' | 'examiner_preparing';
  copy: JourneyCopy;
  isReconnecting: boolean;
};

/** Stable handoff shell shared by waiting-for-agent and agent-preparing. */
function AiExaminerHandoffShell({
  journey,
  copy,
  isReconnecting,
}: AiExaminerHandoffShellProps) {
  const { t } = useTranslation();
  const activateLiveOrb = true; // unified nebula orb should react while agent is in-room
  const showTransportReconnectOverlay = useTransportReconnectOverlayVisible(isReconnecting);

  return (
    <Box flex={1} position="relative" testID="oral-exam-student-ai-examiner-handoff">
      <VStack
        flex={1}
        p="$8"
        space="xl"
        justifyContent="center"
        alignItems="center"
        testID={studentRuntimeJourneyTestId(journey)}
      >
        <Box
          testID="oral-exam-student-ai-examiner-orb-footprint"
          width={AI_EXAMINER_HANDOFF_ORB_SIZE}
          height={AI_EXAMINER_HANDOFF_ORB_SIZE}
          alignItems="center"
          justifyContent="center"
        >
          <AiExaminerPresence
            size={AI_EXAMINER_HANDOFF_ORB_SIZE}
            useLiveVoiceAssistant={activateLiveOrb}
          />
        </Box>
        <VStack space="md" alignItems="center" maxWidth={560}>
          <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
            {t(copy.titleKey, { defaultValue: copy.titleDefault })}
          </Text>
          {copy.bodyKey ? (
            <Text size="md" color="$textBody" textAlign="center">
              {t(copy.bodyKey, { defaultValue: copy.bodyDefault })}
            </Text>
          ) : null}
        </VStack>
      </VStack>
      <ReconnectOverlay visible={showTransportReconnectOverlay} />
    </Box>
  );
}

/** Honest transport / readiness surface for the student exam-room journey. */
export function StudentRuntimeJourneyScreen({
  journey,
  driver,
  isReconnecting = false,
}: StudentRuntimeJourneyScreenProps) {
  const { t } = useTranslation();
  const copy = journeyCopy(journey);
  const transportReconnecting = isReconnecting || journey === 'reconnecting';
  const showTransportReconnectOverlay = useTransportReconnectOverlayVisible(transportReconnecting);

  if (isAiExaminerHandoffJourney(journey, driver)) {
    return (
      <AiExaminerHandoffShell
        journey={journey}
        copy={copy}
        isReconnecting={isReconnecting}
      />
    );
  }

  return (
    <Box flex={1} position="relative">
      <VStack
        flex={1}
        p="$8"
        space="xl"
        justifyContent="center"
        alignItems="center"
        testID={studentRuntimeJourneyTestId(journey)}
      >
        <Spinner size="large" color="$brandBase" />
        <VStack space="md" alignItems="center" maxWidth={560}>
          <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
            {t(copy.titleKey, { defaultValue: copy.titleDefault })}
          </Text>
          {copy.bodyKey ? (
            <Text size="md" color="$textBody" textAlign="center">
              {t(copy.bodyKey, { defaultValue: copy.bodyDefault })}
            </Text>
          ) : null}
        </VStack>
      </VStack>
      <ReconnectOverlay visible={showTransportReconnectOverlay} />
    </Box>
  );
}
